// src/pages/Customer-Page/CustomerTable.js
import React, { useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  MdSearch,
  MdEdit,
  MdDelete,
  MdClear,
  MdFilterList,
} from 'react-icons/md';
import {
  FaUserPlus,
  FaFileExcel,
  FaEye,
  FaBuilding,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaIdCard,
  FaShieldAlt,
} from 'react-icons/fa';
import { HiOutlineViewGrid, HiOutlineViewList } from 'react-icons/hi';
import BASE_URL from '../../api';
import './CustomerTable.css';

const API_BASE_URL = `${BASE_URL}/api/master`;
const DEFAULT_CUSTOMER_ROLE_ID = '698b2f46a6d62379e83e28ca';

// Custom styling for react-select to perfectly match our theme
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    '&:hover': {
      borderColor: '#2563eb',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#2563eb'
      : state.isFocused
      ? '#eff6ff'
      : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    fontSize: '0.875rem',
    cursor: 'pointer',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
  }),
};

const initialFormState = {
  name: '',
  companyName: '',
  email: '',
  mobileNumber: '',
  gstNumber: '',
  panNumber: '',
  password: '',
  contractSignedDate: '',
  contractStartDate: '',
  contractEndDate: '',
  role: '',
  pincode: '',
  branch: '',
  address: '',
  isActive: true,
};

const CustomerTable = () => {
  // Master lists
  const [customers, setCustomers] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Search, Filter & View Controls
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [branchFilter, setBranchFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('card'); // 'card' (Landscape Card View) | 'table' (List View)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});

  // View Customer Details modal
  const [viewCustomer, setViewCustomer] = useState(null);

  // Auth Header Helper
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // -------------------------------------------------------------
  // DATA FETCHING
  // -------------------------------------------------------------
  const fetchCustomers = async () => {
    try {
      let res = await fetch(`${API_BASE_URL}/customer`, { headers: getAuthHeaders() });
      if (!res.ok) {
        res = await fetch(`${API_BASE_URL}/customer`, { headers: { 'Content-Type': 'application/json' } });
      }
      if (!res.ok) throw new Error('Failed to fetch customers');
      const { data } = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error fetching customers');
    }
  };

  const fetchPincodes = async () => {
    try {
      let res = await fetch(`${API_BASE_URL}/pincode`, { headers: getAuthHeaders() });
      if (!res.ok) {
        res = await fetch(`${API_BASE_URL}/pincode`, { headers: { 'Content-Type': 'application/json' } });
      }
      if (!res.ok) return;
      const { data } = await res.json();
      setPincodes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBranches = async () => {
    try {
      let res = await fetch(`${API_BASE_URL}/branch`, { headers: getAuthHeaders() });
      if (!res.ok) {
        res = await fetch(`${API_BASE_URL}/branch`, { headers: { 'Content-Type': 'application/json' } });
      }
      if (!res.ok) return;
      const { data } = await res.json();
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRoles = async () => {
    try {
      let res = await fetch(`${BASE_URL}/api/role/roles`, { headers: getAuthHeaders() });
      if (!res.ok) {
        res = await fetch(`${BASE_URL}/api/role/roles`, { headers: { 'Content-Type': 'application/json' } });
      }
      if (!res.ok) throw new Error('Failed to fetch roles');
      const { data } = await res.json();
      setRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchPincodes(), fetchBranches(), fetchCustomers(), fetchRoles()]);
      setLoading(false);
    };
    if (localStorage.getItem('token')) {
      loadAll();
    }
  }, []);

  // Reset pagination on filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, branchFilter]);

  // -------------------------------------------------------------
  // REACT-SELECT OPTIONS (WITH SEARCH BUILT-IN)
  // -------------------------------------------------------------
  const roleOptions = useMemo(() => {
    return roles.map((r) => ({
      value: r._id,
      label: r.roleName,
    }));
  }, [roles]);

  const pincodeOptions = useMemo(() => {
    return pincodes.map((p) => ({
      value: p._id,
      label: `${p.pincode} - ${p.locationname || 'N/A'}`,
      pincode: p.pincode,
      locationname: p.locationname,
    }));
  }, [pincodes]);

  const branchOptions = useMemo(() => {
    return branches.map((b) => ({
      value: b._id,
      label: `${b.branchname} (${b.branchcode || 'N/A'})`,
      branchname: b.branchname,
      branchcode: b.branchcode,
    }));
  }, [branches]);

  // Default customer role finder
  const defaultRoleId = useMemo(() => {
    const found = roles.find((r) => r.roleName?.toLowerCase() === 'customer');
    return found?._id || DEFAULT_CUSTOMER_ROLE_ID;
  }, [roles]);

  // -------------------------------------------------------------
  // VALIDATION & CRUD
  // -------------------------------------------------------------
  const validateForm = () => {
    const errors = {};
    if (!form.name?.trim()) errors.name = 'Customer Name is required';
    if (!form.companyName?.trim()) errors.companyName = 'Company Name is required';

    if (!form.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = 'Invalid email address';
    }

    if (!form.mobileNumber?.trim()) {
      errors.mobileNumber = 'Mobile Number is required';
    } else if (!/^\d{10}$/.test(form.mobileNumber.trim())) {
      errors.mobileNumber = 'Mobile must be 10 digits';
    }

    if (form.gstNumber?.trim()) {
      const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstRegex.test(form.gstNumber.trim().toUpperCase())) {
        errors.gstNumber = 'Invalid GSTIN format (e.g. 22AAAAA0000A1Z5)';
      }
    }

    if (!form.contractSignedDate) errors.contractSignedDate = 'Contract Signed Date is required';
    if (!form.contractStartDate) errors.contractStartDate = 'Contract Start Date is required';
    if (!form.contractEndDate) errors.contractEndDate = 'Contract End Date is required';

    if (!editingCustomer && !form.password?.trim()) {
      errors.password = 'Password is required for new customers';
    } else if (!editingCustomer && form.password.trim().length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!form.role) errors.role = 'Role is required';
    if (!form.pincode) errors.pincode = 'Pincode is required';
    if (!form.branch) errors.branch = 'Branch is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenModal = (customer = null) => {
    setFormErrors({});
    if (customer) {
      setEditingCustomer(customer);
      setForm({
        name: customer.name || '',
        companyName: customer.companyName || '',
        email: customer.email || '',
        mobileNumber: customer.mobileNumber || '',
        gstNumber: customer.gstNumber || '',
        panNumber: customer.panNumber || '',
        password: '',
        contractSignedDate: customer.contractSignedDate ? customer.contractSignedDate.split('T')[0] : '',
        contractStartDate: customer.contractStartDate ? customer.contractStartDate.split('T')[0] : '',
        contractEndDate: customer.contractEndDate ? customer.contractEndDate.split('T')[0] : '',
        role: customer.role?._id || customer.role || defaultRoleId,
        pincode: customer.pincode?._id || customer.pincode || '',
        branch: customer.branch?._id || customer.branch || '',
        address: customer.address || '',
        isActive: customer.isActive !== false && customer.status !== 'Inactive',
      });
    } else {
      setEditingCustomer(null);
      setForm({
        ...initialFormState,
        role: defaultRoleId,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCustomer(null);
    setFormErrors({});
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.warn('Please fill all mandatory fields properly');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        companyName: form.companyName.trim(),
        email: form.email.trim().toLowerCase(),
        mobileNumber: form.mobileNumber.trim(),
        gstNumber: form.gstNumber ? form.gstNumber.trim().toUpperCase() : '',
        panNumber: form.panNumber ? form.panNumber.trim().toUpperCase() : '',
        contractSignedDate: form.contractSignedDate,
        contractStartDate: form.contractStartDate,
        contractEndDate: form.contractEndDate,
        role: form.role || defaultRoleId,
        pincode: form.pincode,
        branch: form.branch,
        address: form.address.trim(),
        isActive: form.isActive,
        status: form.isActive ? 'Active' : 'Inactive',
      };

      if (!editingCustomer) {
        payload.password = form.password;
      } else if (form.password?.trim()) {
        payload.password = form.password.trim();
      }

      const url = editingCustomer
        ? `${API_BASE_URL}/customer/${editingCustomer._id}`
        : `${API_BASE_URL}/customer`;
      const method = editingCustomer ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || 'Operation failed');
      }

      toast.success(editingCustomer ? 'Customer updated successfully!' : 'Customer created successfully!');
      handleCloseModal();
      fetchCustomers();
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: 'Delete Customer?',
      text: `Are you sure you want to delete ${name || 'this customer'}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE_URL}/customer/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to delete customer');
        toast.success('Customer deleted successfully');
        fetchCustomers();
      } catch (err) {
        toast.error(err.message || 'Failed to delete');
      }
    }
  };

  // -------------------------------------------------------------
  // EXPORT EXCEL
  // -------------------------------------------------------------
  const handleExportExcel = () => {
    if (customers.length === 0) {
      toast.info('No customer data to export');
      return;
    }

    const exportRows = filteredCustomers.map((c, idx) => ({
      'S.No': idx + 1,
      'Customer Code': c.code || '-',
      'Customer Name': c.name || '-',
      'Company Name': c.companyName || '-',
      Email: c.email || '-',
      'Mobile Number': c.mobileNumber || '-',
      'GST Number': c.gstNumber || '-',
      'PAN Number': c.panNumber || '-',
      Branch: c.branch?.branchname || '-',
      Pincode: c.pincode?.pincode || '-',
      Location: c.pincode?.locationname || '-',
      Address: c.address || '-',
      'Contract Signed Date': c.contractSignedDate ? c.contractSignedDate.split('T')[0] : '-',
      'Contract Start Date': c.contractStartDate ? c.contractStartDate.split('T')[0] : '-',
      'Contract End Date': c.contractEndDate ? c.contractEndDate.split('T')[0] : '-',
      Status: c.isActive !== false && c.status !== 'Inactive' ? 'Active' : 'Inactive',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
    XLSX.writeFile(workbook, `Customer_Master_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Customer list exported to Excel successfully!');
  };

  // -------------------------------------------------------------
  // FILTER & SEARCH LOGIC
  // -------------------------------------------------------------
  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      // 1. Status Filter
      const isActive = c.isActive !== false && c.status !== 'Inactive';
      if (statusFilter === 'ACTIVE' && !isActive) return false;
      if (statusFilter === 'INACTIVE' && isActive) return false;

      // 2. Branch Filter
      if (branchFilter !== 'ALL') {
        const branchId = c.branch?._id || c.branch;
        if (branchId !== branchFilter) return false;
      }

      // 3. Search Query
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase().trim();

      const nameMatch = (c.name || '').toLowerCase().includes(term);
      const companyMatch = (c.companyName || '').toLowerCase().includes(term);
      const emailMatch = (c.email || '').toLowerCase().includes(term);
      const mobileMatch = (c.mobileNumber || '').includes(term);
      const codeMatch = (c.code || '').toLowerCase().includes(term);
      const gstMatch = (c.gstNumber || '').toLowerCase().includes(term);
      const panMatch = (c.panNumber || '').toLowerCase().includes(term);
      const branchMatch = (c.branch?.branchname || '').toLowerCase().includes(term);
      const pincodeMatch = (c.pincode?.pincode || '').includes(term);
      const locMatch = (c.pincode?.locationname || '').toLowerCase().includes(term);

      return (
        nameMatch ||
        companyMatch ||
        emailMatch ||
        mobileMatch ||
        codeMatch ||
        gstMatch ||
        panMatch ||
        branchMatch ||
        pincodeMatch ||
        locMatch
      );
    });
  }, [customers, searchTerm, statusFilter, branchFilter]);

  // Pagination calculation
  const totalItems = filteredCustomers.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCustomers.slice(start, start + pageSize);
  }, [filteredCustomers, currentPage, pageSize]);

  // Metrics counters
  const totalActive = useMemo(
    () => customers.filter((c) => c.isActive !== false && c.status !== 'Inactive').length,
    [customers]
  );
  const totalInactive = customers.length - totalActive;

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ================= PAGE HEADER & KPI METRICS ================= */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2">
            Customer Master
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Manage client profiles, contracts, locations & credentials
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition shadow-sm"
          >
            <FaFileExcel className="text-green-600 text-base" />
            <span>Export Excel</span>
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition shadow-md hover:shadow-lg"
          >
            <FaUserPlus className="text-base" />
            <span>Add New Customer</span>
          </button>
        </div>
      </div>

      {/* KPI Stats Quick Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Total Clients</p>
            <p className="text-2xl font-bold text-gray-800">{customers.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
            <FaBuilding />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Active Accounts</p>
            <p className="text-2xl font-bold text-green-600">{totalActive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center font-bold">
            <FaCheckCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Inactive</p>
            <p className="text-2xl font-bold text-gray-500">{totalInactive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center font-bold">
            <FaTimesCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Branches</p>
            <p className="text-2xl font-bold text-indigo-600">{branches.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
            <FaMapMarkerAlt />
          </div>
        </div>
      </div>

      {/* ================= MAIN CONTENT CARD ================= */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6 mb-8">
        {/* TOOLBAR: Search Bar, Dropdown Filters & View Switcher */}
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 mb-6 pb-5 border-b border-gray-200">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[280px]">
            <MdSearch className="absolute left-3.5 top-3 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by customer, company, email, mobile, branch, GST..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                <MdClear className="text-lg" />
              </button>
            )}
          </div>

          {/* Filter Dropdowns & View Mode Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>

            {/* Branch Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Branch:</span>
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 max-w-[170px]"
              >
                <option value="ALL">All Branches</option>
                {branches.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.branchname}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters (if active) */}
            {(searchTerm || statusFilter !== 'ALL' || branchFilter !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                  setBranchFilter('ALL');
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1 underline"
              >
                Reset
              </button>
            )}

            {/* View Mode Toggle: Landscape Card vs Table List */}
            <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200 ml-auto lg:ml-2">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'card'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Landscape Card View"
              >
                <HiOutlineViewGrid className="text-base" />
                <span className="hidden sm:inline">Card View</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'table'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Table List View"
              >
                <HiOutlineViewList className="text-base" />
                <span className="hidden sm:inline">List View</span>
              </button>
            </div>
          </div>
        </div>

        {/* Result Summary Bar */}
        <div className="flex justify-between items-center mb-4 text-xs font-medium text-gray-500">
          <span>
            Showing <strong className="text-gray-800">{paginatedData.length}</strong> of{' '}
            <strong className="text-gray-800">{filteredCustomers.length}</strong> customers
            {filteredCustomers.length !== customers.length && ` (filtered from ${customers.length} total)`}
          </span>
          <span className="hidden sm:inline text-gray-400">
            Current layout: <strong className="text-blue-600">{viewMode === 'card' ? 'Landscape Cards' : 'Data Table'}</strong>
          </span>
        </div>

        {/* ================= CONTENT: LANDSCAPE CARD VIEW ================= */}
        {loading ? (
          <div className="py-20 text-center text-gray-400 flex flex-col items-center justify-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm font-semibold">Loading customers...</p>
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="py-16 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <FaBuilding className="text-4xl text-gray-300 mx-auto mb-2" />
            <p className="text-base font-bold text-gray-700">No customers found</p>
            <p className="text-xs text-gray-400 mt-1">
              Try adjusting your search query or filters, or add a new customer.
            </p>
          </div>
        ) : viewMode === 'card' ? (
          /* LANDSCAPE CARDS GRID */
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-6">
            {paginatedData.map((c, idx) => {
              const isActive = c.isActive !== false && c.status !== 'Inactive';
              const sNo = (currentPage - 1) * pageSize + idx + 1;
              const initials = (c.companyName || c.name || 'C')
                .split(' ')
                .map((w) => w[0])
                .slice(0, 2)
                .join('')
                .toUpperCase();

              return (
                <div
                  key={c._id || idx}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:border-blue-400 customer-card-hover p-5 flex flex-col justify-between transition-all"
                >
                  {/* Top Bar of Landscape Card */}
                  <div>
                    <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        {/* Company Badge / Initials */}
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold text-base flex items-center justify-center shadow-sm flex-shrink-0">
                          {initials}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-gray-900 text-base leading-tight">
                              {c.companyName || c.name}
                            </h3>
                            {c.code && (
                              <span className="text-[11px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                {c.code}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 font-medium mt-0.5 flex items-center gap-1.5">
                            <span>Contact:</span>
                            <strong className="text-gray-700">{c.name}</strong>
                            <span className="text-gray-300">•</span>
                            <span className="text-gray-400">#{sNo}</span>
                          </p>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${
                          isActive
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-red-50 text-red-600 border-red-200'
                        }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${
                            isActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                          }`}
                        ></span>
                        {isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>

                    {/* Landscape Middle: 3 Structured Columns */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-3.5 bg-gray-50/70 p-3 rounded-lg border border-gray-100 text-xs">
                      {/* Col 1: Contact Details */}
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                          Contact
                        </span>
                        <div className="flex items-center gap-1.5 text-gray-700 truncate" title={c.email}>
                          <FaEnvelope className="text-blue-500 flex-shrink-0" />
                          <span className="truncate">{c.email || '-'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <FaPhone className="text-emerald-500 flex-shrink-0" />
                          <span>{c.mobileNumber || '-'}</span>
                        </div>
                      </div>

                      {/* Col 2: Hub & Territory */}
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                          Branch & Hub
                        </span>
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <FaBuilding className="text-indigo-500 flex-shrink-0" />
                          <span className="font-semibold text-gray-800">
                            {c.branch?.branchname || '-'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-700 truncate">
                          <FaMapMarkerAlt className="text-rose-500 flex-shrink-0" />
                          <span className="truncate">
                            {c.pincode?.pincode
                              ? `${c.pincode.pincode} (${c.pincode.locationname || ''})`
                              : '-'}
                          </span>
                        </div>
                      </div>

                      {/* Col 3: Contract Validity */}
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                          Contract Validity
                        </span>
                        <div className="flex items-center gap-1.5 text-gray-700">
                          <FaCalendarAlt className="text-amber-500 flex-shrink-0" />
                          <span>
                            {c.contractStartDate ? c.contractStartDate.split('T')[0] : 'N/A'}
                          </span>
                        </div>
                        <div className="text-[11px] text-gray-500">
                          to <strong className="text-gray-700">{c.contractEndDate ? c.contractEndDate.split('T')[0] : 'N/A'}</strong>
                        </div>
                      </div>
                    </div>

                    {/* Tax tags & Address */}
                    <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
                      {c.gstNumber && (
                        <span className="bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded font-mono text-[11px]">
                          GST: <strong className="text-gray-800">{c.gstNumber}</strong>
                        </span>
                      )}
                      {c.panNumber && (
                        <span className="bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded font-mono text-[11px]">
                          PAN: <strong className="text-gray-800">{c.panNumber}</strong>
                        </span>
                      )}
                      {c.address && (
                        <span className="text-gray-500 text-xs truncate max-w-[280px]" title={c.address}>
                          📍 {c.address}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bottom Action Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-2">
                    <span className="text-[11px] text-gray-400">
                      Role: <strong className="text-gray-600">{c.role?.roleName || 'Customer'}</strong>
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setViewCustomer(c)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                        title="View Details"
                      >
                        <FaEye />
                        <span>View</span>
                      </button>
                      <button
                        onClick={() => handleOpenModal(c)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition"
                        title="Edit Customer"
                      >
                        <MdEdit />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(c._id, c.companyName || c.name)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                        title="Delete Customer"
                      >
                        <MdDelete />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ================= TABLE LIST VIEW (MATCHING ALL MASTERS) ================= */
          <div className="overflow-x-auto rounded-lg border border-gray-200 mb-6">
            <table className="w-full text-left text-sm text-gray-600 border-collapse">
              <thead className="bg-blue-600 text-white text-xs uppercase font-semibold">
                <tr>
                  <th className="py-3 px-4 text-center w-14">S.No</th>
                  <th className="py-3 px-4">Customer / Company</th>
                  <th className="py-3 px-4">Contact Details</th>
                  <th className="py-3 px-4">Branch & Location</th>
                  <th className="py-3 px-4">Contract Period</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedData.map((c, idx) => {
                  const isActive = c.isActive !== false && c.status !== 'Inactive';
                  const sNo = (currentPage - 1) * pageSize + idx + 1;

                  return (
                    <tr key={c._id || idx} className="hover:bg-blue-50/50 transition">
                      <td className="py-3.5 px-4 text-center text-gray-500 font-semibold">{sNo}</td>

                      <td className="py-3.5 px-4 font-medium text-gray-800">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                            {(c.companyName || c.name || 'C').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 leading-tight">
                              {c.companyName || c.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {c.name} {c.code && <span className="text-blue-600">({c.code})</span>}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="text-gray-800 font-medium">{c.email}</div>
                        <div className="text-xs text-gray-500">{c.mobileNumber}</div>
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-gray-800">{c.branch?.branchname || '-'}</div>
                        <div className="text-xs text-gray-500">
                          {c.pincode?.pincode
                            ? `${c.pincode.pincode} (${c.pincode.locationname || ''})`
                            : '-'}
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-xs text-gray-600">
                        <div>
                          Start: <strong className="text-gray-800">{c.contractStartDate ? c.contractStartDate.split('T')[0] : '-'}</strong>
                        </div>
                        <div>
                          End: <strong className="text-gray-800">{c.contractEndDate ? c.contractEndDate.split('T')[0] : '-'}</strong>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                            isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-600' : 'bg-red-600'}`}></span>
                          {isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        <div className="flex justify-center items-center gap-1.5">
                          <button
                            onClick={() => setViewCustomer(c)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="View Details"
                          >
                            <FaEye className="text-base" />
                          </button>
                          <button
                            onClick={() => handleOpenModal(c)}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition"
                            title="Edit"
                          >
                            <MdEdit className="text-lg" />
                          </button>
                          <button
                            onClick={() => handleDelete(c._id, c.companyName || c.name)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Delete"
                          >
                            <MdDelete className="text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ================= PAGINATION CONTROLS ================= */}
        {totalPages > 0 && (
          <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-600">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500 font-semibold"
              >
                {[6, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span>entries per page</span>
              <span className="hidden md:inline ml-3 text-gray-400">
                (Page {currentPage} of {totalPages})
              </span>
            </div>

            {/* Prev / Page Number / Next Buttons */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border border-gray-300 font-semibold text-xs transition disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>

              {/* Number buttons */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => {
                  // Show pages around current
                  return p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1;
                })
                .map((pageNum, idx, arr) => {
                  const showEllipsis = idx > 0 && pageNum - arr[idx - 1] > 1;
                  return (
                    <React.Fragment key={pageNum}>
                      {showEllipsis && <span className="px-1 text-gray-400">...</span>}
                      <button
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 rounded-lg text-xs font-bold transition ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1.5 rounded-lg border border-gray-300 font-semibold text-xs transition disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ================= LANDSCAPE CARD MODAL: ADD / EDIT CUSTOMER ================= */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden animate-fadeIn">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <FaBuilding className="text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">
                    {editingCustomer ? `Edit Customer: ${editingCustomer.companyName || editingCustomer.name}` : 'Add New Customer'}
                  </h3>
                  <p className="text-blue-100 text-xs">
                    Fill in complete customer profile, contract validity, and location setup
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full transition"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            {/* Modal Form Scrollable Area */}
            <form onSubmit={handleSubmitForm} className="p-6 overflow-y-auto custom-modal-scroll space-y-6 flex-1">
              {/* LANDSCAPE CARD 1: Basic Information */}
              <div className="bg-gray-50/80 p-5 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-1.5 h-5 bg-blue-600 rounded-full"></span>
                  <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                    Basic Information
                  </h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {/* Customer Name */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Customer Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. John Doe"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.name ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                    )}
                  </div>

                  {/* Company Name */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Acme Logistics Pvt Ltd"
                      value={form.companyName}
                      onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.companyName ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.companyName && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.companyName}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="e.g. contact@acme.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.email ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                    )}
                  </div>

                  {/* Mobile Number */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={10}
                      placeholder="10-digit number"
                      value={form.mobileNumber}
                      onChange={(e) => setForm({ ...form, mobileNumber: e.target.value.replace(/\D/g, '') })}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.mobileNumber ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.mobileNumber && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.mobileNumber}</p>
                    )}
                  </div>

                  {/* GST Number */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      GST Number
                    </label>
                    <input
                      type="text"
                      maxLength={15}
                      placeholder="22AAAAA0000A1Z5"
                      value={form.gstNumber}
                      onChange={(e) => setForm({ ...form, gstNumber: e.target.value.toUpperCase() })}
                      className={`w-full px-3 py-2 border rounded-lg text-sm uppercase outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.gstNumber ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.gstNumber && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.gstNumber}</p>
                    )}
                  </div>

                  {/* PAN Number */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      PAN Number
                    </label>
                    <input
                      type="text"
                      maxLength={10}
                      placeholder="ABCDE1234F"
                      value={form.panNumber}
                      onChange={(e) => setForm({ ...form, panNumber: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Password Field */}
                  <div className={editingCustomer ? 'md:col-span-3' : ''}>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      {editingCustomer ? 'Reset Password (Optional)' : 'Password *'}
                    </label>
                    <input
                      type="password"
                      placeholder={editingCustomer ? 'Leave blank to keep existing password' : 'Min 6 characters'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.password ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.password && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* LANDSCAPE CARD 2: Contract Dates */}
              <div className="bg-gray-50/80 p-5 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-1.5 h-5 bg-blue-600 rounded-full"></span>
                  <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                    Contract Dates
                  </h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Signed Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={form.contractSignedDate}
                      onChange={(e) => setForm({ ...form, contractSignedDate: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.contractSignedDate ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.contractSignedDate && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.contractSignedDate}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={form.contractStartDate}
                      onChange={(e) => setForm({ ...form, contractStartDate: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.contractStartDate ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.contractStartDate && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.contractStartDate}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={form.contractEndDate}
                      onChange={(e) => setForm({ ...form, contractEndDate: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.contractEndDate ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.contractEndDate && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.contractEndDate}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* LANDSCAPE CARD 3: System, Location & Address (WITH SEARCHABLE DROPDOWNS!) */}
              <div className="bg-gray-50/80 p-5 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-blue-600 rounded-full"></span>
                    <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                      System & Location (Searchable Dropdowns)
                    </h4>
                  </div>
                  {/* Active Status Checkbox */}
                  <label className="flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span>Account Active</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {/* Searchable Role Dropdown */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={roleOptions}
                      value={roleOptions.find((r) => r.value === form.role) || null}
                      onChange={(opt) => setForm({ ...form, role: opt ? opt.value : '' })}
                      placeholder="Search & select role..."
                      isClearable={false}
                      styles={customSelectStyles}
                    />
                    {formErrors.role && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.role}</p>
                    )}
                  </div>

                  {/* Searchable Pincode Dropdown */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Pincode & City <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={pincodeOptions}
                      value={pincodeOptions.find((p) => p.value === form.pincode) || null}
                      onChange={(opt) => setForm({ ...form, pincode: opt ? opt.value : '' })}
                      placeholder="Search pincode or city..."
                      isClearable
                      styles={customSelectStyles}
                    />
                    {formErrors.pincode && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.pincode}</p>
                    )}
                  </div>

                  {/* Searchable Branch Dropdown */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Branch <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={branchOptions}
                      value={branchOptions.find((b) => b.value === form.branch) || null}
                      onChange={(opt) => setForm({ ...form, branch: opt ? opt.value : '' })}
                      placeholder="Search branch name or code..."
                      isClearable
                      styles={customSelectStyles}
                    />
                    {formErrors.branch && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.branch}</p>
                    )}
                  </div>

                  {/* Address Textarea */}
                  <div className="md:col-span-3">
                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                      Full Billing / Office Address
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Door No, Street name, Area, City..."
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>{editingCustomer ? 'Update Customer' : 'Create Customer'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= LANDSCAPE DETAILS MODAL: VIEW CUSTOMER ================= */}
      {viewCustomer && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center font-bold text-white text-lg">
                  {(viewCustomer.companyName || viewCustomer.name || 'C').charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-lg font-bold">{viewCustomer.companyName || viewCustomer.name}</h3>
                  <p className="text-xs text-blue-100">
                    {viewCustomer.code || 'Customer Profile'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewCustomer(null)}
                className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto custom-modal-scroll text-sm">
              {/* Basic Info Landscape Card */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <h4 className="text-xs font-bold text-blue-700 uppercase mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span> Contact & Identity
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">Customer Name</span>
                    <strong className="text-gray-800 text-sm">{viewCustomer.name}</strong>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">Email</span>
                    <strong className="text-blue-600 text-sm">{viewCustomer.email}</strong>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">Mobile</span>
                    <strong className="text-gray-800 text-sm">{viewCustomer.mobileNumber}</strong>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">GST Number</span>
                    <strong className="text-gray-800">{viewCustomer.gstNumber || '-'}</strong>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">PAN Number</span>
                    <strong className="text-gray-800">{viewCustomer.panNumber || '-'}</strong>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">Status</span>
                    <span
                      className={`inline-block px-2 py-0.5 rounded font-bold text-[11px] ${
                        viewCustomer.isActive !== false && viewCustomer.status !== 'Inactive'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {viewCustomer.isActive !== false && viewCustomer.status !== 'Inactive'
                        ? 'ACTIVE'
                        : 'INACTIVE'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Branch & Location Landscape Card */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <h4 className="text-xs font-bold text-blue-700 uppercase mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span> Territory & Location
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">Assigned Branch</span>
                    <strong className="text-gray-800 text-sm">
                      {viewCustomer.branch?.branchname || '-'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">Pincode</span>
                    <strong className="text-gray-800 text-sm">
                      {viewCustomer.pincode?.pincode || '-'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">Area / City</span>
                    <strong className="text-gray-800 text-sm">
                      {viewCustomer.pincode?.locationname || '-'}
                    </strong>
                  </div>
                  <div className="col-span-2 md:col-span-3">
                    <span className="text-gray-400 block uppercase text-[10px]">Full Address</span>
                    <p className="text-gray-700 mt-0.5">{viewCustomer.address || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Contract Information Landscape Card */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <h4 className="text-xs font-bold text-blue-700 uppercase mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span> Contract Validity
                </h4>
                <div className="grid grid-cols-3 gap-4 text-xs">
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">Signed Date</span>
                    <strong className="text-gray-800">
                      {viewCustomer.contractSignedDate ? viewCustomer.contractSignedDate.split('T')[0] : '-'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">Start Date</span>
                    <strong className="text-gray-800">
                      {viewCustomer.contractStartDate ? viewCustomer.contractStartDate.split('T')[0] : '-'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-gray-400 block uppercase text-[10px]">End Date</span>
                    <strong className="text-gray-800">
                      {viewCustomer.contractEndDate ? viewCustomer.contractEndDate.split('T')[0] : '-'}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setViewCustomer(null)}
                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-semibold transition"
              >
                Close
              </button>
              <button
                onClick={() => {
                  const target = viewCustomer;
                  setViewCustomer(null);
                  handleOpenModal(target);
                }}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold transition shadow"
              >
                Edit Customer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTable;